import type { Node, Edge } from '../../../core/models/scene';
import { isOrgPrimaryReportingEdge, type OrgRoutingPolicyEdgeLike } from '../connectors/routingPolicies';

type ReportingNode = Pick<Node, 'id' | 'type'>;
type ReportingEdge = Pick<Edge, 'id' | 'from' | 'to'> & OrgRoutingPolicyEdgeLike;

export type OrgReportCounts = {
  directReports: number;
  totalReports: number;
};

export type OrgReportingManagerReference = {
  personId: string;
  name: string;
  label: string;
};

const PERSON_NODE_TYPES = new Set(['org_person', 'org_person_photo', 'org_person_photo_left', 'org_person_photo_right', 'org_person_photo_inner_left', 'org_person_text']);
const POSITION_NODE_TYPES = new Set(['org_position', 'org_open_seat']);

function getEdgeRelation(edge: OrgRoutingPolicyEdgeLike): string | undefined {
  const attrs = (edge.attrs || {}) as Record<string, unknown>;
  return typeof attrs.relation === 'string' ? attrs.relation : undefined;
}

export function isReportsToEdge(edge: OrgRoutingPolicyEdgeLike): boolean {
  const attrs = (edge.attrs || {}) as Record<string, unknown>;
  if (attrs.dotted) return false;
  return isOrgPrimaryReportingEdge(edge);
}

export function isMatrixEdge(edge: Edge): boolean {
  const edgeType = edge.type as unknown as string;
  if (edgeType === 'matrix') return true;
  const relation = getEdgeRelation(edge);
  return relation === 'matrix' || relation === 'matrix_reports';
}

const REPORTING_CHAIN_EDGE_TYPES = new Set(['reporting', 'reports_to']);

const isReportingEdgeForFilterChain = (edge: Edge): boolean => {
  const edgeType = edge.type as unknown as string;
  return REPORTING_CHAIN_EDGE_TYPES.has(edgeType);
};

export function computeReportingChain(
  matchingNodeIds: Set<string>,
  edges: Edge[],
): { chainNodeIds: Set<string>; chainEdgeIds: Set<string> } {
  const chainNodeIds = new Set<string>();
  const chainEdgeIds = new Set<string>();

  if (matchingNodeIds.size === 0) {
    return { chainNodeIds, chainEdgeIds };
  }

  const managerBySubordinate = new Map<string, { managerId: string; edgeId: string }>();

  for (const edge of edges) {
    if (isReportingEdgeForFilterChain(edge)) {
      managerBySubordinate.set(edge.from, { managerId: edge.to, edgeId: edge.id });
    }
  }

  for (const nodeId of matchingNodeIds) {
    let currentId: string | undefined = nodeId;
    const visited = new Set<string>();

    while (currentId && !visited.has(currentId)) {
      visited.add(currentId);

      const managerInfo = managerBySubordinate.get(currentId);
      if (!managerInfo) {
        break;
      }

      if (!matchingNodeIds.has(managerInfo.managerId)) {
        chainNodeIds.add(managerInfo.managerId);
      }
      chainEdgeIds.add(managerInfo.edgeId);

      currentId = managerInfo.managerId;
    }
  }

  return { chainNodeIds, chainEdgeIds };
}

function isOccupiesEdge(edge: ReportingEdge): boolean {
  const edgeType = edge.type as unknown as string;
  if (edgeType === 'occupies') return true;
  return getEdgeRelation(edge) === 'occupies';
}

export function computeOrgReportingTargets(nodes: readonly ReportingNode[], edges: readonly ReportingEdge[]): Map<string, string> {
  const nodeIdSet = new Set(nodes.map((node) => node.id));
  const nodeTypeById = new Map(nodes.map((node) => [node.id, node.type]));

  const occupiesByPerson = new Map<string, string>();
  edges.forEach((edge) => {
    if (!isOccupiesEdge(edge)) return;
    if (!nodeIdSet.has(edge.from) || !nodeIdSet.has(edge.to)) return;
    occupiesByPerson.set(edge.from, edge.to);
  });

  const targets = new Map<string, string>();
  nodeIdSet.forEach((nodeId) => {
    const nodeType = nodeTypeById.get(nodeId);
    if (!nodeType) {
      targets.set(nodeId, nodeId);
      return;
    }
    if (POSITION_NODE_TYPES.has(nodeType)) {
      targets.set(nodeId, nodeId);
      return;
    }
    if (PERSON_NODE_TYPES.has(nodeType)) {
      const positionId = occupiesByPerson.get(nodeId);
      if (positionId && nodeIdSet.has(positionId)) {
        targets.set(nodeId, positionId);
        return;
      }
    }
    targets.set(nodeId, nodeId);
  });

  return targets;
}

/**
 * Resolves the current primary manager label for every rendered Person or
 * Position. Reporting is canonical relationship data, so this is deliberately
 * derived for read-only consumers such as assistant filters rather than copied
 * back into a `reportsTo` record property.
 */
export function computeOrgReportingManagerLabels(
  nodes: readonly Node[],
  edges: readonly Edge[],
): Map<string, string> {
  const nodeIdSet = new Set(nodes.map((node) => node.id));
  const reportingTargets = computeOrgReportingTargets(nodes, edges);
  const managerByReportingId = new Map<string, string>();
  const labelByReportingId = new Map<string, string>();

  const labelFor = (node: Node): string | null => {
    const attrs = (node.attrs ?? {}) as Record<string, unknown>;
    const props = (node.props ?? {}) as Record<string, unknown>;
    for (const value of [node.name, attrs.name, props.name, attrs.title, props.title]) {
      if (typeof value === 'string' && value.trim()) return value.trim();
    }
    return null;
  };

  for (const node of nodes) {
    const label = labelFor(node);
    if (!label) continue;
    const reportingId = reportingTargets.get(node.id) ?? node.id;
    // A person occupying a Position provides the useful manager name for the
    // People Chart; otherwise the Position label remains the fallback.
    if (!labelByReportingId.has(reportingId) || PERSON_NODE_TYPES.has(node.type)) {
      labelByReportingId.set(reportingId, label);
    }
  }

  for (const edge of edges) {
    if (!isReportsToEdge(edge)) continue;
    const childId = reportingTargets.get(edge.from) ?? edge.from;
    const managerId = reportingTargets.get(edge.to) ?? edge.to;
    if (!nodeIdSet.has(childId) || !nodeIdSet.has(managerId) || childId === managerId) continue;
    managerByReportingId.set(childId, managerId);
  }

  const result = new Map<string, string>();
  for (const node of nodes) {
    const reportingId = reportingTargets.get(node.id) ?? node.id;
    const managerId = managerByReportingId.get(reportingId);
    const managerLabel = managerId ? labelByReportingId.get(managerId) : undefined;
    if (managerLabel) result.set(node.id, managerLabel);
  }
  return result;
}

/**
 * Resolves the stable Person ID for each node's primary manager. This keeps
 * the filter condition unambiguous when multiple people share a display name.
 */
export function computeOrgReportingManagerReferences(
  nodes: readonly Node[],
  edges: readonly Edge[],
): Map<string, OrgReportingManagerReference> {
  const nodeIdSet = new Set(nodes.map((node) => node.id));
  const reportingTargets = computeOrgReportingTargets(nodes, edges);
  const managerByReportingId = new Map<string, string>();
  const personByReportingId = new Map<string, OrgReportingManagerReference>();
  const positionLabelById = new Map<string, string>();

  const stringField = (node: Node, keys: string[]): string | null => {
    const attrs = (node.attrs ?? {}) as Record<string, unknown>;
    const props = (node.props ?? {}) as Record<string, unknown>;
    for (const key of keys) {
      for (const value of [attrs[key], props[key]]) {
        if (typeof value === 'string' && value.trim()) return value.trim();
      }
    }
    return null;
  };

  for (const node of nodes) {
    const reportingId = reportingTargets.get(node.id) ?? node.id;
    if (POSITION_NODE_TYPES.has(node.type)) {
      const title = typeof node.name === 'string' && node.name.trim()
        ? node.name.trim()
        : stringField(node, ['title', 'name']);
      if (title) positionLabelById.set(reportingId, title);
      continue;
    }
    if (!PERSON_NODE_TYPES.has(node.type)) continue;

    // Source-backed projections retain their opaque record key as `personId`
    // and expose the CSV Person ID as `employeeId`. Prefer the value people
    // see in the properties panel and only fall back to the record key.
    const personId = stringField(node, ['employeeId', 'personId']) ?? node.id;
    const name = typeof node.name === 'string' && node.name.trim()
      ? node.name.trim()
      : stringField(node, ['name']) ?? personId;
    const role = stringField(node, ['role', 'title', 'positionTitle']);
    if (role && !positionLabelById.has(reportingId)) {
      positionLabelById.set(reportingId, role);
    }
    personByReportingId.set(reportingId, { personId, name, label: name });
  }

  for (const edge of edges) {
    if (!isReportsToEdge(edge)) continue;
    const childId = reportingTargets.get(edge.from) ?? edge.from;
    const managerId = reportingTargets.get(edge.to) ?? edge.to;
    if (!nodeIdSet.has(childId) || !nodeIdSet.has(managerId) || childId === managerId) continue;
    managerByReportingId.set(childId, managerId);
  }

  const result = new Map<string, OrgReportingManagerReference>();
  for (const node of nodes) {
    const reportingId = reportingTargets.get(node.id) ?? node.id;
    const managerId = managerByReportingId.get(reportingId);
    if (!managerId) continue;
    const manager = personByReportingId.get(managerId);
    if (!manager) continue;
    const positionTitle = positionLabelById.get(managerId);
    result.set(node.id, {
      ...manager,
      label: positionTitle
        ? `${manager.name} - ${positionTitle} - ${manager.personId}`
        : `${manager.name} - ${manager.personId}`,
    });
  }
  return result;
}

export function computeOrgReportCounts(nodes: readonly ReportingNode[], edges: readonly ReportingEdge[]): Map<string, OrgReportCounts> {
  const nodeIdSet = new Set(nodes.map((node) => node.id));
  const reportingTargets = computeOrgReportingTargets(nodes, edges);
  const resolveReportingNodeId = (nodeId: string): string => reportingTargets.get(nodeId) ?? nodeId;

  const directReportsByManager = new Map<string, Set<string>>();
  edges.forEach((edge) => {
    // Matrix reporting is an overlay relationship, not part of the primary
    // management tree. Including it here makes secondary managers appear to
    // own direct reports in every footer/count consumer.
    if (!isReportsToEdge(edge)) return;
    const fromId = resolveReportingNodeId(edge.from);
    const toId = resolveReportingNodeId(edge.to);
    if (!nodeIdSet.has(fromId) || !nodeIdSet.has(toId)) return;
    if (fromId === toId) return;
    const directReports = directReportsByManager.get(toId) ?? new Set<string>();
    directReports.add(fromId);
    directReportsByManager.set(toId, directReports);
  });

  const countsByReportingId = new Map<string, OrgReportCounts>();
  directReportsByManager.forEach((directReports, managerId) => {
    const visited = new Set<string>([managerId]);
    const stack = Array.from(directReports);
    while (stack.length > 0) {
      const current = stack.pop();
      if (!current || visited.has(current)) continue;
      visited.add(current);
      const next = directReportsByManager.get(current);
      if (next) {
        next.forEach((child) => {
          if (!visited.has(child)) stack.push(child);
        });
      }
    }
    countsByReportingId.set(managerId, {
      directReports: directReports.size,
      totalReports: visited.size - 1,
    });
  });

  const result = new Map<string, OrgReportCounts>();
  nodes.forEach((node) => {
    if (!POSITION_NODE_TYPES.has(node.type) && !PERSON_NODE_TYPES.has(node.type)) return;
    const reportingId = resolveReportingNodeId(node.id);
    const counts = countsByReportingId.get(reportingId) ?? { directReports: 0, totalReports: 0 };
    result.set(node.id, counts);
  });

  return result;
}
